<template>
  <div class="line-chart">
    <loading-overlay v-if="$asyncComputed.data.updating"></loading-overlay>
    <error-overlay v-else-if="$asyncComputed.data.error"></error-overlay>
      <div v-if="!config.isSingleVarPlot">
        <div class="controls" ref="controls" v-if="config.lineId.indexOf('dynamic')>=0">
          <multi-select-component mode="single" v-model="currentCol"  label="column" :options="columnOptions" :configurationOptions="{'allow-empty': false, 'label-inside':true}"/>
          <multi-select-component mode="multiple" v-model="currentSegments"  label="segments" :options="segmentOptions" :configurationOptions="{'allow-empty': false, 'label-inside':true}"/>
        </div>
        <div v-else class="controls">
          <multi-select-component mode="multiple" v-model="currentVentricles"  label="ventricle" :options="ventricleOptions" :configurationOptions="{'allow-empty': false, 'label-inside':true}"/>
        </div>
    </div>
    <div class="charts-wrapper">
      <div class="legend" ref="legend" v-if="config.legend">
        <div class="title">{{config.legend}}</div>
        <div class="legend-item" v-for="(line, lineIndex) in linesToPlot" :key="line.id" 
          :style="{borderColor: color(line.id),opacity:(config.lineId.indexOf('dynamic')>=0 && currentSegment)?(currentSegment.id===line.id?1:0.3):1, fontWeight: lineIndex == currentSensorIndex ? '600': '400'}"
          @click="() => onLegendLineClick(line)"
          >
          {{line.name || line.id}}
        </div>
      </div>
      <svg ref="svg" :width="chartSize.width" :height="chartSize.height"></svg>
      <div class="tooltip-container" ref="tooltipContainer" :style="{margin: margin.top+'px '+margin.left+'px'}"></div>
      <ECGChart v-if="ecgData && showECG" :data="ecgData" v-model="localActualCurrentTime" 
        :width="ecgSize.width" 
        :height="ecgSize.height" 
        :xAxis="true"
        :yAxis="true"
        :forcedMargin="ecgMargin"
        ></ECGChart>
    </div>
  </div>
</template>

<script>
import widgetContent from '@/mixins/widgetContent'
import d3 from '@/utils/d3importer'
import { padLinear, formatValue } from '@/utils'
import { SECONDARY_COLOR } from '@/utils/colorScales'
import Api from '@/api'
import ECGChart from '@/components/CaseDashboard/ECGChart'
import MultiSelectComponent from '@/components/MultiSelectComponent'

export default {
  props: {
    currentTime: Number,
    actualCurrentTime: Number,
    currentSegment: {
      default: null,
    },
    showECG: {
      default: true,
    },
    config: Object,
    lineData: {
      type: Array,
      default: null
    },
  },
  // model: {
  //   prop: 'actualCurrentTime',
  //   event: 'change'
  // },
  mixins: [widgetContent],
  components: {
    ECGChart,
    MultiSelectComponent,
  },

  data() {
    return {
      currentSegments: [1],
      currentCol: null,
      x: d3.scaleLinear(),
      y: d3.scaleLinear(),
      linesToPlot: null,
      currentVentricles: [0, 1],
      currentSensorIndex: 0,
    }
  },
  mounted() {
    if (this.data) {
      this.createLinesData()
      this.updateChart()
      this.updateHandle()
    }
  },
  computed: {
    localActualCurrentTime: {
      get() {
        return this.actualCurrentTime
      },
      set(newCurrentTime) {
        this.$emit('change-time', newCurrentTime)
      },
    },
    chartSize() {
      // let controlsHeight = this.$refs.controls?this.$refs.controls.clientHeight:0
      let legendHeight = this.$refs.legend ? this.$refs.legend.clientHeight : 0
      return {
        width: Math.max(0, this.size.width),
        height: Math.max(0, (this.size.height - legendHeight) * 0.75),
      }
    },
    contentSize() {
      return {
        width: Math.max(0, this.chartSize.width - this.margin.left - this.margin.right),
        height: Math.max(0, this.chartSize.height - this.margin.top - this.margin.bottom),
      }
    },
    ecgSize() {
      return {
        width: this.chartSize.width,
        height: this.size.height * 0.25,
      }
    },
    margin() {
      let l
      if (
        this.data &&
        this.linesToPlot &&
        String(Math.floor(d3.max(this.linesToPlot.map(d => d.data).flat(), d => +d.y))).length > 3
      )
        l = 85
      else l = 65
      if (this.hasXAxis) return { left: l, top: 10, bottom: 45, right: 20 }
      else return { left: l, top: 10, bottom: this.showECG ? 20 : 0, right: 20 }
    },
    ecgMargin() {
      return { left: this.margin.left, right: this.margin.right }
    },
    columnOptions() {
      // Nodeberia hacer falta...
      if (!this.data) return []
      let headers
      if (this.config.lineId.indexOf('dynamic') >= 0) headers = this.data[0].segmentData.headers
      else if (this.config.dataSource === 'ecgline' || this.config.dataSource === 'linedata') headers = [this.config.lineId.split('-')[1]]
      else 
        if (this.data.length) headers = this.data[0].headers
        else headers = this.data.headers
      let headerOpts = headers.map(h => {
        return {
          value: h,
          id: h,
          label: h,
        }
      })
      if (!this.config.dataSource === 'ecgline' || !this.config.dataSource === 'linedata') headerOpts.shift()
      return headerOpts
    },
    segmentOptions() {
      return [...Array(32)].map((i, idx) => idx + 1)
    },
    ventricleOptions() {
      return [
        { value: 0, label: 'Left Ventricle' },
        { value: 1, label: 'Right Ventricle' },
      ]
    },
    hasXAxis() {
      return this.config.lineId.indexOf('-') > 0 && this.config.lineId.split('-')[0] !== 'time'
    },
    color() {
      if (!this.colorsUsed) this.colorsUsed = []
      let lineIds = this.linesToPlot.map(d => d.id)
      // Truere de colors els que ja no hi son
      this.colorsUsed.forEach((d, pos) => {
        let idx = lineIds.findIndex(c => {
          return c === d
        })
        if (idx < 0) {
          this.colorsUsed[pos] = undefined
        }
      })

      // Afegir els que arrriben
      lineIds.forEach(d => {
        let idx = this.colorsUsed.findIndex(c => {
          return c === d
        })
        if (idx < 0) {
          let newPos = this.colorsUsed.findIndex(c => {
            return c === undefined
          })
          if (newPos === -1) {
            this.colorsUsed.push(d)
          } else {
            this.colorsUsed[newPos] = d
          }
        }
      })
      let color = id => {
        let idx = this.colorsUsed.findIndex(c => c === id)
        return [SECONDARY_COLOR].concat(d3.schemePaired)[idx % 12]
      }
      return color
    },
    headers() {
      if (this.config.dataSource === 'segments_json') return this.data[0].segmentData.headers
      else if (this.config.dataSource === 'lines_json') return this.data[0].headers
      else if (this.config.dataSource === 'sparkline') return this.data.headers
      else if (this.config.dataSource === 'ecgline') return this.$store.state.paramsData.subject.sparkline.headers
      else if (this.config.dataSource === 'linedata') return [this.config.lineId.split('-')[1]]
    },
    units() {
      if (this.config.dataSource === 'segments_json') return this.data[0].segmentData.units
      else if (this.config.dataSource === 'lines_json') return this.data[0].units
      else if (this.config.dataSource === 'sparkline') return this.data.units
      else if (this.config.dataSource === 'ecgline') return this.$store.state.paramsData.subject.sparkline.units
      else if (this.config.dataSource === 'linedata') return [this.config.units]
    },
    attr_idx() {
      let vars = this.config.lineId.split('-')
      let x_idx = this.headers.map(t => t.toLowerCase()).indexOf(vars[0]) //Chapuzilla porque "a veces" mandan Time en vez de time...
      let y_idx =
        vars[1] === 'dynamic' || 'leads'
          ? this.headers.indexOf(this.currentCol)
          : this.headers.indexOf(vars[1])
      return { x: x_idx, y: y_idx }
    },
    time_idx() {
      return this.headers.indexOf('Time')
    },
    dynamicCurrentLineIndex() {
      let c = 0
      if (this.currentSegment && this.config.lineId.indexOf('dynamic') >= 0) {
        c = this.linesToPlot.findIndex(line => this.currentSegment.id === line.id)
      } else {
        c = this.currentSensorIndex
      }
      return Math.min(c, this.linesToPlot.length)
    },
  },
  asyncComputed: {
    data() {
      if (this.config.dataSource === 'segments_json')
        return Api.getSegments(this.$store.state.paramsData)
      else if (this.config.dataSource === 'lines_json')
        return Api.getLines(this.$store.state.paramsData)
      else if (this.config.dataSource ==='sparkline')
        return this.$store.state.paramsData.subject.sparkline
      else if (this.config.dataSource === 'ecgline')
        return Api.getEcg(this.$store.state.paramsData)
      else if (this.config.dataSource === 'linedata')
        return this.lineData
    },
    ecgData() {
      if (this.showECG) return Api.getEcg(this.$store.state.paramsData)
      else return []
    },
  },

  methods: {
    createLinesData() {
      let data
      if (!this.currentCol) return
      if (this.config.isSingleVarPlot) {
        if(this.config.dataSource === 'sparkline') {
          let singleVarData =  [{
            id: 0,
            name: '',
            data: this.$store.state.paramsData.subject.sparkline.rows.map(row => {
              return {
                  dt: row[this.time_idx],
                  x: row[this.attr_idx.x],
                  y: row[this.attr_idx.y],
              }
            })
          }]
          this.linesToPlot = singleVarData
        } else if (this.config.dataSource === 'linedata') {
          let singleVarData = [{
            id: 0,
            name:  this.config.lineId.split('-')[1],
            data: this.lineData.map(point => {
              return {
                dt: point.dt,
                x: point.dt,
                y: point.value}
            })
          }]
          this.linesToPlot = singleVarData
        } else if (this.config.dataSource === 'ecgline') {
          let selectedVarData = this.extractLineData(this.config.lineId.split('-')[1])
          let singleVarData = [{
            id: 0,
            name:  this.config.lineId.split('-')[1],
            data: selectedVarData.map(point => {
              return {
                dt: point.dt,
                x: point.dt,
                y: point.value}
            })
          }]
          this.linesToPlot = singleVarData
        }
      }
      else {
        if (this.config.lineId.indexOf('dynamic') < 0) {
            let vents = this.currentVentricles
            data = this.data.filter(d => vents.indexOf(d.id) >= 0)
            // let skip = Math.floor(this.data.rows.length/1500) || 1
            // data = this.data.rows.filter((d,i)=>i%skip===0).map((row)=>{
            //   return {
            //     dt: +row[this.time_idx],
            //     x: +row[this.attr_idx.x],
            //     y: +row[this.attr_idx.y]
            //   }
            // })
            // this.linesToPlot = [{id:this.config.lineId,data:data}]
            let linesToPlot = data.map(vd => {
              let skip = Math.floor(vd.rows.length / 1500) || 1
              let segRows = vd.rows.filter((d, i) => i % skip === 0)
              return {
                id: vd.id,
                name: vd.name,
                data: segRows.map(row => {
                  return {
                    dt: +row[this.time_idx],
                    x: +row[this.attr_idx.x],
                    y: +row[this.attr_idx.y],
                  }
                }),
              }
            })
            this.linesToPlot = linesToPlot
        } else {
            let segs = this.currentSegments
            data = this.data.filter(d => segs.indexOf(d.segmentId) >= 0)
            let linesToPlot = data.map(sd => {
              let skip = Math.floor(sd.segmentData.rows.length / 1500) || 1
              let segRows = sd.segmentData.rows.filter((d, i) => i % skip === 0)
              return {
                id: sd.segmentId,
                data: segRows.map(row => {
                  return {
                    dt: +row[0],
                    x: +row[this.attr_idx.x],
                    y: +row[this.attr_idx.y],
                  }
                }),
              }
            })
            this.linesToPlot = linesToPlot
          }
      }
      // this.linesToPlot.forEach((d)=>{
      //   d.data = d.data.filter((dd,i)=>i%100===0)
      // })
      this.currentSensorIndex = Math.min(this.currentSensorIndex, this.linesToPlot.length - 1)
    },
    skip() {
        return Math.floor(this.ecgData.length/1500) || 1
    },
    extractLineData(lineName) {
        return this.ecgData.filter((d,i)=>i%this.skip()===0).map((d,idx)=>({dt:d['Time'],value:d[lineName]}))
    },
    getYlabel() {
      if (this.config.lineId.indexOf('dynamic') < 0) {
        let name = this.config.lineId.split('-')[1]
        let unit = this.units[this.headers.indexOf(name)]
        if (unit) return `${name} (${unit})`
        else return `${name}`
      } else {
        let name = this.currentCol
        let unit = this.units[this.headers.indexOf(this.currentCol)]
        if (unit) return `${name} (${unit})`
        else return `${name}`
      }
    },
    getXlabel() {
      let name = this.config.lineId.split('-')[0]
      let unit = this.units[this.headers.indexOf(name)]
      if (unit) return `${name} (${unit})`
      else return `${name}`
    },
    updateChart() {
      if (!this.currentCol) return
      let root = d3.select(this.$refs.svg)

      if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
        this.$refs.svg.createSVGPoint = null
      }
      // BACKGROUND
      let background = root.selectAll('.background').data([null])
      let backgroundEnter = background
        .enter()
        .append('rect')
        .attr('class', 'background')
        .attr('opacity', 0)
      background = background.merge(backgroundEnter)
      background
        .attr('transform', `translate(${this.margin.left},${this.margin.top})`)
        .attr('width', this.contentSize.width)
        .attr('height', this.contentSize.height)
      if (this.config.lineId.indexOf('dynamic') >= 0) {
        backgroundEnter.on('click', () => {
          this.$emit('change-segment', null)
        })
      }

      ////////////////////////
      let allLines = this.linesToPlot.map(d => d.data).flat()
      let x = this.x.range([0, this.contentSize.width]).domain(d3.extent(allLines, d => +d.x))
      let y = this.y.range([this.contentSize.height, 0]).domain(
        padLinear(
          d3.extent(allLines, d => +d.y),
          0
        )
      )

      // let ext = y.domain()[1] - y.domain()[0]
      // y.domain([y.domain()[0]-ext*0.1,y.domain()[1]+ext*0.1])

      //AXIS
      let yAxis = root.selectAll('.y-axis').data([this.currentCol], d => d)
      yAxis.exit().remove()
      let yAxisEnter = yAxis
        .enter()
        .append('g')
        .attr('class', 'axis y-axis')
      yAxisEnter
        .append('text')
        .attr('class', 'label')
        .text(this.getYlabel())
      yAxis = yAxis.merge(yAxisEnter)
      yAxis
        .attr('transform', `translate(${this.margin.left},${this.margin.top})`)
        .call(d3.axisLeft(y))
      yAxis
        .select('.label')
        .attr(
          'transform',
          `translate(-${this.margin.left - 30},${this.contentSize.height / 2}) rotate(-90)`
        )

      if (this.hasXAxis) {
        let xAxis = root.selectAll('.x-axis').data([this.currentCol], d => d)
        xAxis.exit().remove()
        let xAxisEnter = xAxis
          .enter()
          .append('g')
          .attr('class', 'axis x-axis')
        xAxisEnter
          .append('text')
          .attr('class', 'label')
          .text(this.getXlabel())
        xAxis = xAxis.merge(xAxisEnter)
        xAxis
          .attr(
            'transform',
            `translate(${this.margin.left},${this.contentSize.height + this.margin.top})`
          )
          .call(d3.axisBottom(x))
        xAxis
          .select('.label')
          .attr('transform', `translate(${this.contentSize.width / 2},${this.margin.bottom - 10})`)
      }

      // GRID
      let grid = root.selectAll('.grid').data([null])
      let gridEnter = grid
        .enter()
        .append('g')
        .attr('class', 'grid')
      gridEnter
        .selectAll('.x-line')
        .data(x.ticks(10))
        .enter()
        .append('line')
        .attr('class', 'x-line')
      gridEnter
        .selectAll('.y-line')
        .data(y.ticks(6))
        .enter()
        .append('line')
        .attr('class', 'y-line')
      grid = grid.merge(gridEnter)
      grid.attr('transform', `translate(${this.margin.left},${this.margin.top})`)
      grid
        .selectAll('.y-line')
        .attr('x1', 0)
        .attr('x2', this.contentSize.width)
        .attr('y1', d => y(d))
        .attr('y2', d => y(d))
      grid
        .selectAll('.x-line')
        .attr('y1', 0)
        .attr('y2', this.contentSize.height)
        .attr('x1', d => x(d))
        .attr('x2', d => x(d))

      // LINES
      let content = root.selectAll('.content').data([null])
      content = content.merge(
        content
          .enter()
          .append('g')
          .attr('class', 'content')
      )
      content.attr('transform', `translate(${this.margin.left},${this.margin.top})`)
      let lines = content.selectAll('.line').data(this.linesToPlot, d => d.id)

      lines.exit().remove()
      let linesEnter = lines
        .enter()
        .append('g')
        .attr('class', 'line')
      linesEnter
        .append('path')
        .attr('class', 'main')
        .attr('fill', 'none')
        .attr('stroke-width', 2)
      if (this.config.lineId.indexOf('dynamic') >= 0) {
        linesEnter
          .append('path')
          .attr('class', 'ghost')
          .attr('fill', 'none')
          .attr('stroke-width', 8)
          .attr('stroke', this.config.lineColor ? this.config.lineColor : '#fff')
          .attr('opacity', 0)
        linesEnter.attr('cursor', 'pointer').on('click', d => {
          if (this.currentSegment !== d) this.$emit('change-segment', d)
          else this.$emit('change-segment', null)
        })
      }
      lines = lines.merge(linesEnter)

      let line = d3
        .line()
        .x(function(d) {
          return x(d.x)
        })
        .y(function(d) {
          return y(d.y)
        })

      lines.select('.main').attr('stroke', d => this.config.lineColor ? this.config.lineColor : this.color(d.id))
      lines.select('.main').attr('d', d => line(d.data))
      lines.select('.ghost').attr('d', d => line(d.data))
      if (this.config.lineId.indexOf('dynamic') >= 0) {
        lines.attr('opacity', d => {
          if (!this.currentSegment) return 1
          else if (this.currentSegment.id === d.id) return 1
          else return 0.2
        })
      }
      this.updateHandle()
    },
    updateHandle() {
      if (!this.linesToPlot) return
      let content = d3.select(this.$refs.svg).select('.content')
      let handleCoords = content.selectAll('.handle-coords').data([null])
      handleCoords = handleCoords.merge(
        handleCoords
          .enter()
          .append('path')
          .attr('class', 'handle-coords')
      )
      let handle = content.selectAll('.handle').data([null])
      let handleColor = this.color(this.linesToPlot[this.dynamicCurrentLineIndex].id)
      handle = handle.merge(
        handle
          .enter()
          .append('circle')
          .attr('class', 'handle')
          .attr('r', 3)
      )
      handle.attr('fill', handleColor)
      let tooltipContainer = d3.select(this.$refs.tooltipContainer)
      let tooltip = tooltipContainer.selectAll('.tooltip').data([null])
      let tooltipEnter = tooltip
        .enter()
        .append('div')
        .attr('class', 'tooltip')
      tooltipEnter.append('div').attr('class', 'text')
      tooltip = tooltip.merge(tooltipEnter)

      if (this.config.lineId.split('-')[0] === 'time') {
        let sensor = d3
          .select(this.$refs.svg)
          .selectAll('.sensor')
          .data([null])
        let sensorEnter = sensor
          .enter()
          .append('g')
          .attr('class', 'sensor')
        sensorEnter.append('rect').attr('fill', 'transparent')

        sensorEnter.call(
          d3.drag().on('start drag', () => {
            let dragX = Math.max(
              this.x.range()[0],
              Math.min(this.x.range()[1], d3.event.x - this.margin.left)
            )
            this.$emit('change-time', this.x.invert(dragX))
          })
        )
        sensor
          .merge(sensorEnter)
          .select('rect')
          .attr('width', this.contentSize.width)
          .attr('height', this.contentSize.height)
          .attr('transform', `translate(${this.margin.left},${this.margin.top})`)
      }

      // NO INTERPOLA, quizas deberia...
      let nearestDataPoint = this.linesToPlot[this.dynamicCurrentLineIndex].data.find(
        d => d.dt >= this.currentTime
      )
      let currentX = nearestDataPoint ? nearestDataPoint.x : 0
      let currentY = nearestDataPoint ? nearestDataPoint.y : 0
      handle.attr('transform', `translate(${this.x(currentX)},${this.y(currentY)})`)
      handleCoords.attr(
        'd',
        `M 0 ${this.y(currentY)} H ${this.x(currentX)} M ${this.x(currentX)} ${
          this.y.range()[0]
        } V ${this.y(currentY)}`
      )
      tooltip.style('top', this.y(currentY) + 'px').style('left', this.x(currentX) + 'px')
      tooltip.select('.text').text(this.tooltipText(currentX, currentY))
    },

    tooltipText(x, y) {
      x = parseFloat(x).toFixed(2)
      y = parseFloat(y).toFixed(2)
      if (this.hasXAxis) {
        return `${formatValue(x)} ${this.units[this.attr_idx.x]} / ${formatValue(y)} ${
          this.units[this.attr_idx.y]
        }`
      } else {
        return `${formatValue(y)} ${this.units[this.attr_idx.y]}`
      }
    },
    onLegendLineClick(line) {
      this.currentSensorIndex = this.linesToPlot.findIndex(l => l.id === line.id)
      if (this.config.lineId.indexOf('dynamic') >= 0) {
        if (this.currentSegment === line) this.$emit('change-segment', null)
        else this.$emit('change-segment', line)
      }
    },
  },
  watch: {
    data() {
      this.createLinesData()
      this.updateChart()
    },
    currentSegments() {
      this.createLinesData()
      this.updateChart()
    },
    currentVentricles() {
      this.createLinesData()
      this.updateChart()
    },
    columnOptions() {
      if (!this.currentCol) {
        this.currentCol = this.columnOptions[0].id
      }
    },
    currentCol() {
      this.createLinesData()
      this.updateChart()
    },
    currentTime() {
      this.updateHandle()
    },
    currentSegment() {
      // this.updateSlice()
      this.updateChart()
    },
    size() {
      if (this.linesToPlot) {
        this.updateChart()
        this.updateHandle()
      }
    },
    margin() {
      if (this.linesToPlot) {
        this.updateChart()
        this.updateHandle()
      }
    },
    currentSensorIndex() {
      this.updateChart()
    },
  },
}
</script>

<style lang="stylus" scoped>
.line-chart
  position: relative
  display: flex
  flex-direction: column
  overflow: visible !important // ?? para el tooltip...
  background: #fff
  user-select: none

  .controls
    position: absolute
    right: 60px
    z-index: 2
    display: flex
    flex: 0 0 auto
    flex-wrap: wrap
    justify-content: flex-end
    align-items: flex-end
    max-width: 100%
    transform: translate(0, -47px) // GUARRO GUARRO.... :D

    .multi-select-component
      margin: 0 5px

  .charts-wrapper
    position: absolute
    display: flex
    flex: 1 1 auto
    flex-direction: column
    width: 100%
    height: 100%

    svg
      flex: 1 1 75%

      // flex 1 1 auto
      >>> .grid
        line
          stroke-width: 1px
          stroke: #f0f0f0

      >>> .handle-coords
        stroke: #aaa
        stroke-dasharray: 5 10
        stroke-width: 1px
        fill: none

      >>> .axis
        .tick
          line
            stroke: #aaa

          text
            fill: #aaa

        .domain
          stroke: #aaa

        .label
          font-size: 12px
          fill: #000
          text-anchor: middle

    .tooltip-container
      position: absolute

      >>> .tooltip
        position: absolute
        padding: 2px 7px
        background: #fff
        box-shadow: 0 0 5px rgba(0, 0, 0, 0.2)
        white-space: nowrap
        transform: translate(-50%, -10px)
        m-tooltip-tip()

    .legend
      display: flex
      // padding: 10px
      align-items: center
      margin: auto

      .title
        flex: 0 0 auto
        color: #888
        font-weight: 200

      .legend-item
        margin: 4px 10px
        min-width: 1.2em
        border-top: solid 3px
        text-align: center
        cursor: pointer
        // padding-top 3px

    .ecg-chart
      flex: 1 1 25%
</style>